// Pure vote-balance engine — the arithmetic an agent would do by hand on the
// result sheet before signing it. No I/O, no React: fully unit-testable.
//
// Two independent figures on the same sheet must agree:
//   the declared "Valid Votes" box (Result Summary step), and
//   the sum of the per-party scores (Party Vote Count step).
// Plus the sheet's own ballot account, which must close on its own terms.
//
// This is a distinct axis from parent-vs-children reconciliation
// (`reconciliation/engine.ts`) — that one compares an entity's uploaded result
// against its children's. This one stays inside a single unsubmitted result.

import type { ResultType } from '../../../constants/global';
import { type IReconThresholds, type ReconSeverity } from '../../../interface/reconciliation';
import { severityOf, worstSeverity } from '../reconciliation/engine';
import type { IChildCode, ICode, IPartyVotes, IResultSummary } from './utils';

/** Which way the count is off. `balanced` means exactly equal. */
export type BalanceDirection = 'balanced' | 'short' | 'over';

/**
 * `notStarted` — nothing to check yet (no declared figure, or no votes recorded).
 * `awaitingCoverage` — a collation level with children still outstanding; a
 *   running total exists but the verdict is deliberately withheld.
 * `balanced` / `off` — the check ran and gave an answer.
 */
export type BalanceState = 'notStarted' | 'awaitingCoverage' | 'balanced' | 'off';

/** One child entity's contribution to the grand total (collation levels only). */
export interface IChildBalance {
	id: number;
	code: string;
	name: string;
	subtotal: number;
	/** True once the agent has saved votes for this child. */
	recorded: boolean;
}

/** One line of the sheet's own ballot account, stated as the equation it is. */
export interface ISheetCheck {
	key: string;
	label: string;
	/** Human-readable arithmetic, e.g. "156 = 150 + 6". */
	expression: string;
	ok: boolean;
	/** The summary fields this check reads — the jump target when it fails. */
	fields: (keyof IResultSummary)[];
}

export interface IVoteBalance {
	/** Valid Votes as typed on the Result Summary step. */
	declared: number;
	/** Sum of every party score across every entity. */
	counted: number;
	/** counted − declared. Negative = short, positive = over. */
	difference: number;
	direction: BalanceDirection;
	severity: ReconSeverity;
	state: BalanceState;
	/** Non-null only at collation levels (EC8B/C/D). */
	coverage: { recorded: number; total: number } | null;
	/** Per-child subtotals, ordered as the child list is. Empty at EC8A. */
	children: IChildBalance[];
	/** Ballot-account checks that had enough data to run. */
	checks: ISheetCheck[];
	/** True when any ballot-account check failed. */
	hasSheetIssue: boolean;
	/** True when the agent must acknowledge before submitting. */
	needsAcknowledgement: boolean;
}

/**
 * Inside ONE result sheet, exact equality is the requirement — you cannot have
 * half a ballot. So `absFloor` is 0 here, unlike DEFAULT_RECON_THRESHOLDS, which
 * tolerates a 1-vote rounding slip because it compares aggregates from different
 * sources. A single stray vote is still only `minor`; the band scales with the gap.
 */
export const SHEET_THRESHOLDS: IReconThresholds = {
	okPct: 0,
	minorPct: 0.02,
	absFloor: 0,
	ruleVersion: 'vote-balance-1',
};

const num = (v: unknown): number => {
	const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
	return Number.isFinite(n) ? n : 0;
};

/** Sum one entity's party scores, tolerating the NaN a cleared input can leave. */
export const subtotalOf = (entry: IPartyVotes | undefined | null): number =>
	(entry?.votes ?? []).reduce((sum, v) => sum + num(v.votes), 0);

/** Grand total across every entity — the figure that compares to Valid Votes. */
export const countedTotal = (partyVotes: IPartyVotes[] | undefined | null): number =>
	(partyVotes ?? []).reduce((sum, entry) => sum + subtotalOf(entry), 0);

/**
 * Build the ballot-account checks. A check is omitted entirely when every field
 * it reads is still zero — a blank form must never look like a failing one.
 */
export const sheetChecks = (s: IResultSummary): ISheetCheck[] => {
	const register = num(s.DataVotersOnRegister);
	const accredited = num(s.DataAccreditedVoters);
	const issued = num(s.DataBallotPapersIssuedToPoolingUnit);
	const unused = num(s.DataUnusedBallotPapers);
	const used = num(s.DataTotalUsedBallotPapers);
	const rejected = num(s.DataRejectedBallot);
	const valid = num(s.DataTotalValidVotes);

	const out: ISheetCheck[] = [];
	const any = (...v: number[]) => v.some((n) => n > 0);

	if (any(used, valid, rejected)) {
		out.push({
			key: 'usedBallots',
			label: 'Used ballot papers should equal Valid + Rejected',
			expression: `${used} = ${valid} + ${rejected}`,
			ok: used === valid + rejected,
			fields: ['DataTotalUsedBallotPapers', 'DataTotalValidVotes', 'DataRejectedBallot'],
		});
	}

	if (any(issued, unused, used)) {
		out.push({
			key: 'ballotAccount',
			label: 'Issued minus unused should equal used',
			expression: `${issued} − ${unused} = ${used}`,
			ok: issued - unused === used,
			fields: ['DataBallotPapersIssuedToPoolingUnit', 'DataUnusedBallotPapers', 'DataTotalUsedBallotPapers'],
		});
	}

	if (any(register, accredited)) {
		out.push({
			key: 'accreditation',
			label: 'Accredited voters cannot exceed the register',
			expression: `${accredited} ≤ ${register}`,
			ok: accredited <= register,
			fields: ['DataAccreditedVoters', 'DataVotersOnRegister'],
		});
	}

	if (any(accredited, used)) {
		out.push({
			key: 'turnout',
			label: 'Used ballot papers cannot exceed accredited voters',
			expression: `${used} ≤ ${accredited}`,
			ok: used <= accredited,
			fields: ['DataTotalUsedBallotPapers', 'DataAccreditedVoters'],
		});
	}

	return out;
};

export interface IVoteBalanceInput {
	partyVotes: IPartyVotes[] | undefined | null;
	resultSummary: IResultSummary;
	/** The child list for collation levels; ignored at EC8A. */
	childCodes?: IChildCode[] | null;
	resultType?: ResultType | string;
	/** Used only to label a single-entity level's row. */
	selectedParentCode?: ICode | null;
}

export const computeVoteBalance = ({ partyVotes, resultSummary, childCodes, resultType }: IVoteBalanceInput): IVoteBalance => {
	const isCollation = resultType !== 'EC8A';
	const declared = num(resultSummary?.DataTotalValidVotes);
	const counted = countedTotal(partyVotes);
	const difference = counted - declared;

	const byId = new Map((partyVotes ?? []).map((p) => [p.id, p]));
	const children: IChildBalance[] = isCollation
		? (childCodes ?? []).map((c) => ({
				id: c.codeId,
				code: c.code,
				name: c.name,
				subtotal: subtotalOf(byId.get(c.codeId)),
				recorded: !!c.status,
			}))
		: [];

	const coverage = isCollation ? { recorded: children.filter((c) => c.recorded).length, total: children.length } : null;

	const checks = sheetChecks(resultSummary);
	const hasSheetIssue = checks.some((c) => !c.ok);

	const direction: BalanceDirection = difference === 0 ? 'balanced' : difference < 0 ? 'short' : 'over';

	// The verdict needs a declared figure to compare against and at least one
	// recorded vote; and at collation levels, every child in.
	const awaitingCoverage = !!coverage && coverage.total > 0 && coverage.recorded < coverage.total;
	let state: BalanceState;
	if (declared <= 0 || counted <= 0) state = 'notStarted';
	else if (awaitingCoverage) state = 'awaitingCoverage';
	else state = difference === 0 ? 'balanced' : 'off';

	// A failing ballot account is a transcription error worth flagging, but it
	// does not itself move votes — the party gap is what escalates to critical.
	const partySeverity: ReconSeverity = state === 'off' ? severityOf(declared, counted, SHEET_THRESHOLDS) : 'reconciled';
	const severity = worstSeverity([partySeverity, hasSheetIssue ? 'minor' : 'reconciled']);

	return {
		declared,
		counted,
		difference,
		direction,
		severity,
		state,
		coverage,
		children,
		checks,
		hasSheetIssue,
		// A held verdict is never a blocker — the agent is mid-entry.
		needsAcknowledgement: state === 'off' || hasSheetIssue,
	};
};
